// Library methods
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ThemeProvider, createTheme, useMediaQuery } from "@mui/material";

// MUI Components
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";

// Assets
import eyeLogo from "../../../../../assets/images/logos/eyelogo.png";

// Utilities
import {
  FixationLossMethodOptions,
  getTutorialCompletionText,
} from "../../../../../utils/examReportsHelper";
import { useLiveReportData } from "../../../../../hooks/useLiveReporData";
import { ReportEachSectionContext } from "../../../../../contexts/CommentContext";
import { ValidatePDFContext } from "../../../../../contexts/ValidatePDFContext";
import {
  ReportInfoHeaderContent,
  ReportInfoShared,
} from "../../../../../components/UI/ReportInfoShared";
import { ReportInfoEachSectionContext } from "../../../../../contexts/ReportInfoEachSectionContext";
import { availableLanguages } from "../../../../../i18n/languages";
import { DEFAULT_DECIBEL_VALUE_FULL_120 } from "../../../../../enums";
import useClinic from "../../../../../hooks/useClinic";
import { getLocalDateTimeWithZone } from "../../../../../utils/dateHelper";

const ReportInfo = () => {
  // internationalization
  const { t, i18n } = useTranslation();
  const { clinicTimezone } = useClinic();
  const { forBackendPdf } = useContext(ValidatePDFContext);
  const { examReport, eyeReport, duration } = useContext(
    ReportEachSectionContext
  );

  // handling live data
  const { falsePositivePercentage, falseNegative, fixationLossPercentage } =
    useLiveReportData(examReport);

  const theme = createTheme({
    typography: {
      allVariants: {
        fontFamily: ["Arial", "Verdana", "sans-serif"],
      },
    },
  });

  const isWiderThanSmallScreen = useMediaQuery(theme.breakpoints.up("sm"));

  const isEn = useMemo(() => {
    const en = availableLanguages[0];
    const language = i18n.language;
    return language.includes(en);
  }, [i18n.language]);

  const localDateAndTimeWithZone = useMemo(
    () =>
      examReport?.timing?.start
        ? getLocalDateTimeWithZone(examReport.timing.start, clinicTimezone)
        : null,
    [clinicTimezone, examReport?.timing?.start]
  );

  const fixationLossMethod = useMemo(
    () =>
      examReport?.stats?.fixationLossMethod ??
      FixationLossMethodOptions.EyeTracking,
    [examReport?.stats?.fixationLossMethod]
  );
  const gridSizeData = useMemo(
    () => [
      { xs: 5.5, sm: 3, md: 2.8, lg: 2.8, xl: 2.8 },
      { xs: 6.5, sm: 3.6, md: 3.8, lg: isEn ? 3.8 : 4.8, xl: isEn ? 3.8 : 4.7 },
      { xs: 5.5, sm: 2.8, md: 2.8, lg: isEn ? 2.8 : 2.3, xl: isEn ? 2.8 : 2.3 },
      { xs: 6.5, sm: 2.6, md: 2.6, lg: isEn ? 2.6 : 2.1, xl: isEn ? 2.6 : 2.2 },
    ],
    [isEn]
  );
  const infoData = useMemo(
    () => [
      [
        {
          title: t("word_eye"),
          body: eyeReport === "Right" ? t("word_right") : t("word_left"),
        },
        {
          title: t("report_stimulus_size"),
          body: examReport?.exam?.stimulusSize,
        },
        {
          title: t("report_stimulus_intensity"),
          body: `${examReport?.exam?.decibelValue ?? DEFAULT_DECIBEL_VALUE_FULL_120} dB`,
        },
      ],
      [
        { title: t("report_test_mode"), body: t("report_single_intensity") },
        { title: t("report_binocular_fixation_loss"), body: t("word_false") },
        {
          title: t("fixation_loss_method"),
          body:
            fixationLossMethod === FixationLossMethodOptions.EyeTracking
              ? t("eye_tracking")
              : t("word_blind_spot"),
        },
      ],
      [
        { title: t("false_positive"), body: falsePositivePercentage },
        { title: t("false_negative"), body: falseNegative },
        { title: t("fixation_loss"), body: fixationLossPercentage },
      ],
      [
        {
          title: t("report_exam_date"),
          body: localDateAndTimeWithZone?.date ?? t("word_na"),
          isBlack: true,
        },
        {
          title: t("report_exam_time"),
          body: localDateAndTimeWithZone?.time ?? t("word_na"),
          isBlack: true,
        },
        { title: t("report_exam_duration"), body: duration },
        getTutorialCompletionText(
          examReport?.postProcessing?.tutorialCompletionState,
          t
        ) && {
          title: t("word_tutorial"),
          body: getTutorialCompletionText(
            examReport?.postProcessing?.tutorialCompletionState,
            t
          ),
          isBlack: true,
        },
      ],
    ],
    [
      duration,
      examReport?.exam?.decibelValue,
      examReport?.exam?.stimulusSize,
      eyeReport,
      falseNegative,
      falsePositivePercentage,
      fixationLossMethod,
      fixationLossPercentage,
      localDateAndTimeWithZone?.date,
      localDateAndTimeWithZone?.time,
      examReport?.postProcessing?.tutorialCompletionState,
      t,
    ]
  );
  return (
    <ReportInfoEachSectionContext.Provider value={{ hasFourColumns: true }}>
      <ThemeProvider theme={theme}>
        <Grid
          container
          columnSpacing={2}
          my={1}
          sx={{ display: "flex", alignItems: "start" }}
        >
          <Grid container item xs={12}>
            {forBackendPdf && (
              <Grid
                item
                xs={1}
                style={{
                  alignItems: "center",
                }}
              >
                <img
                  src={eyeLogo}
                  alt="eye logo"
                  style={{ width: "100%", height: "auto" }}
                />
              </Grid>
            )}
            <Grid container item xs={forBackendPdf ? 11 : 12} mb={1.5}>
              <Grid
                item
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                border={1}
                xs={12}
              >
                <Grid
                  item
                  sx={{
                    display: "flex",
                    background: "black",
                    width: 70,
                    height: "100%",
                    color: "white",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                  xs={1.5}
                  sm={1}
                >
                  <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                    {eyeReport === "Right" ? "OD" : "OS"}
                  </Typography>
                </Grid>
                <ReportInfoHeaderContent
                  isWiderThanSmallScreen={isWiderThanSmallScreen}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            container
            item
            display="flex"
            justifyContent={{ xs: "space-around", md: "space-between" }}
          >
            <ReportInfoShared data={infoData} gridSizeData={gridSizeData} />
          </Grid>
        </Grid>
      </ThemeProvider>
    </ReportInfoEachSectionContext.Provider>
  );
};

export default ReportInfo;
